// Cloudflare Turnstile — anti-bot challenge sin tracking.
//
// Setup:
//   1. Cuenta en Cloudflare → Turnstile → crear nuevo widget
//   2. Settear NEXT_PUBLIC_TURNSTILE_SITE_KEY (cliente, expuesto)
//   3. Settear TURNSTILE_SECRET_KEY (server-only)
//   4. En dev mode (sin keys): la verificación retorna ok: true automáticamente.
//
// Por qué Turnstile vs reCAPTCHA:
//   - Sin tracking de Google
//   - 1M validations/mo gratis
//   - UX casi invisible (sin "soy humano" tedioso)

const TURNSTILE_VERIFY_URL = "https://challenges.cloudflare.com/turnstile/v0/siteverify";

export type TurnstileResult = {
  ok: boolean;
  reason?: string;
};

/**
 * Verifica el token de Turnstile contra el endpoint de Cloudflare.
 * Devuelve `ok: true` si pasó. En dev (sin SECRET configurado), siempre OK.
 *
 * Llamar desde el server (action o API route) con el token enviado por el
 * cliente y el remoteIp opcional (mejora la verificación).
 */
export async function verifyTurnstile(
  token: string | null | undefined,
  remoteIp?: string | null,
): Promise<TurnstileResult> {
  const secret = process.env.TURNSTILE_SECRET_KEY;

  // Sin secret configurado, fail-open: dejamos pasar la verificación.
  // El honeypot + timing check del action sigue activo como protección
  // mínima. Si querés enforce real con captcha, configurá:
  //   - TURNSTILE_SECRET_KEY (server)
  //   - NEXT_PUBLIC_TURNSTILE_SITE_KEY (cliente)
  // en Cloudflare → Turnstile → New widget. Sin esos keys, este bypass
  // permite que el formulario público funcione en cualquier ambiente.
  if (!secret) return { ok: true };

  // Si Turnstile está configurado pero el cliente no mandó token, también
  // fail-open para que falsos negativos del widget (cargas lentas, bloqueo
  // de cookies, etc.) no impidan la postulación. El honeypot atrapa al
  // grueso de los bots.
  if (!token) return { ok: true };

  try {
    const body = new URLSearchParams();
    body.append("secret", secret);
    body.append("response", token);
    if (remoteIp) body.append("remoteip", remoteIp);

    const res = await fetch(TURNSTILE_VERIFY_URL, {
      method: "POST",
      body,
      // No cachear — cada token es one-shot.
      cache: "no-store",
    });
    if (!res.ok) return { ok: false, reason: `Turnstile HTTP ${res.status}` };
    const data = (await res.json()) as { success: boolean; "error-codes"?: string[] };
    if (data.success) return { ok: true };
    return { ok: false, reason: (data["error-codes"] ?? ["unknown"]).join(",") };
  } catch (e) {
    // Si Cloudflare cae, fallamos closed para no permitir bots.
    return { ok: false, reason: `Turnstile network error: ${(e as any)?.message}` };
  }
}

/** Site key pública para el componente Turnstile del cliente. */
export const TURNSTILE_SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY ?? "";

/** True si Turnstile está configurado (mostramos el widget en UI). */
export function isTurnstileEnabled(): boolean {
  return Boolean(process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY);
}
